import React, { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Mail, Phone, MapPin, Twitter, Github, Linkedin } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return toast.error("Please fill all fields");
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent — we'll get back within 24h");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Could not send message");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
      <div className="mb-12">
        <div className="text-xs uppercase tracking-widest text-blue-600 font-semibold mb-3">Contact</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight">Contact Us</h1>
        <p className="mt-3 text-muted-foreground max-w-lg">Have questions, feedback or a partnership idea? We&apos;d love to hear from you.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-10">
        <div className="rounded-2xl bg-card border border-border p-8">
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block">Name</Label>
              <Input data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your full name" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block">Email</Label>
              <Input data-testid="contact-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@company.com" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block">Message</Label>
              <Textarea data-testid="contact-message" rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Type your message..." />
            </div>
            <Button data-testid="contact-submit" type="submit" disabled={loading} className="rounded-full bg-blue-600 hover:bg-blue-700 text-white w-full">
              {loading ? "Sending..." : "Send Message"}
            </Button>
          </form>
        </div>

        <div className="space-y-6">
          <div className="rounded-2xl bg-card border border-border p-6 flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-950/40 text-blue-600 grid place-items-center"><Mail size={16} /></div>
            <div>
              <div className="text-xs text-muted-foreground">Email</div>
              <a href="mailto:support@simpleinvoice.com" className="font-medium">support@simpleinvoice.com</a>
            </div>
          </div>
          <div className="rounded-2xl bg-card border border-border p-6 flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-950/40 text-blue-600 grid place-items-center"><Phone size={16} /></div>
            <div>
              <div className="text-xs text-muted-foreground">Phone</div>
              <div className="font-medium">+1 (555) 010-2026</div>
            </div>
          </div>
          <div className="rounded-2xl bg-card border border-border p-6 flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-950/40 text-blue-600 grid place-items-center"><MapPin size={16} /></div>
            <div>
              <div className="text-xs text-muted-foreground">Address</div>
              <div className="font-medium">123 Business Street, New York, NY 10001, USA</div>
            </div>
          </div>

          {/* Google Map placeholder */}
          <div className="rounded-2xl overflow-hidden border border-border h-56 relative bg-blue-50 dark:bg-slate-800">
            <iframe
              title="Location"
              className="w-full h-full"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3022.257!2d-73.99!3d40.75!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c258f01!2sNY!5e0!3m2!1sen!2sus!4v1"
              loading="lazy"
            />
          </div>

          <div className="flex gap-3">
            {[["twitter", Twitter], ["github", Github], ["linkedin", Linkedin]].map(([k, Icon]) => (
              <a key={k} href="#" data-testid={`contact-social-${k}`} className="w-10 h-10 rounded-full bg-blue-50 dark:bg-blue-950/30 text-blue-600 grid place-items-center hover:bg-blue-100 dark:hover:bg-blue-950/50 transition-colors">
                <Icon size={16} />
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
